import { useState } from "react";
import { Clock, Save, X, MessageSquare, Lightbulb, ChevronLeft, ChevronRight, Zap } from "lucide-react";
import RoundDownToFiveMinutes from "../../../components/utils/RoundTimeToFiveMinutes";
import dayjs from "dayjs";

const CompleteSession = ({ startTime, handleCompleteSession, handleCancel }) => {
    const defaultSlide = 3;

    // Mock mood data - in real app these would be your actual images
    const moods = [
        { emoji: "😢", label: "Very Bad", color: "from-red-500 to-red-600" },
        { emoji: "😕", label: "Bad", color: "from-orange-500 to-orange-600" },
        { emoji: "😐", label: "Neutral", color: "from-yellow-500 to-yellow-600" },
        { emoji: "😊", label: "Good", color: "from-green-500 to-green-600" },
        { emoji: "😄", label: "Very Good", color: "from-emerald-500 to-emerald-600" },
    ];

    const [session, setSession] = useState({
        end_mood: defaultSlide,
        end_time: new Date(),
        end_fatigue: 5,
        session_feedback: '',
        session_reflection: ''
    });

    const [currentMoodIndex, setCurrentMoodIndex] = useState(defaultSlide);
    const [endFatigue, setEndFatigue] = useState(5); // Default fatigue level
    const currentTime = dayjs(RoundDownToFiveMinutes(new Date()));
    const [endTime, setEndTime] = useState(currentTime.isAfter(dayjs(startTime)) ? currentTime : dayjs(startTime));

    const handleFieldChange = (fieldName, value) => {
        setSession(prev => ({ ...prev, [fieldName]: value }));
    };

    const handleSubmit = () => {
        const parsedStartTime = dayjs(startTime);
        const end_date = (endTime.hour() > parsedStartTime.hour() || (endTime.hour() === parsedStartTime.hour() && endTime.minute() > parsedStartTime.minute()))
            ? parsedStartTime : parsedStartTime.add(1, 'day');

        const newSession = {
            ...session,
            end_mood: currentMoodIndex,
            end_time: end_date.hour(endTime.hour()).minute(endTime.minute()).second(0).millisecond(0).format('YYYY-MM-DDTHH:mm:ssZ'),
            end_fatigue: endFatigue
        };
        handleCompleteSession(newSession);
    };

    const nextMood = () => {
        setCurrentMoodIndex(prev => (prev + 1) % moods.length);
    };

    const prevMood = () => {
        setCurrentMoodIndex(prev => (prev - 1 + moods.length) % moods.length);
    };

    const formatTime = (date) => {
        return new Intl.DateTimeFormat('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        }).format(date);
    };

    const adjustTime = (minutes) => {
        setEndTime(prev => prev.add(minutes, 'minute'));
    };

    const getFatigueLabel = (value) => {
        if (value === 0) return "No fatigue";
        if (value <= 2) return "Very low";
        if (value <= 4) return "Low";
        if (value <= 6) return "Moderate";
        if (value <= 8) return "High";
        return "Very high";
    };

    const getFatigueColor = (value) => {
        if (value <= 2) return "from-green-500 to-green-600";
        if (value <= 4) return "from-yellow-500 to-yellow-600";
        if (value <= 6) return "from-orange-500 to-orange-600";
        if (value <= 8) return "from-red-500 to-red-600";
        return "from-red-600 to-red-700";
    };

    return (
        <div className="fixed inset-0 bg-black-500 flex items-center justify-center p-4 z-50">
            <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-3xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white relative overflow-hidden">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-white transform rotate-12 translate-x-12"></div>
                    </div>
                    <div className="relative">
                        <h2 className="text-2xl font-bold text-center tracking-wide">Complete Session</h2>
                        <p className="p-style text-indigo-100 text-center mt-1">Reflect on your experience</p>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    {/* Mood Selector */}
                    <div className="mb-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                            <span className="w-2 h-2 bg-indigo-500 rounded-full mr-3"></span>
                            How are you feeling?
                        </h3>
                        
                        <div className="relative">
                            {/* Mood Display */}
                            <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 relative overflow-hidden">
                                <div className={`absolute inset-0 bg-gradient-to-r ${moods[currentMoodIndex].color} opacity-10`}></div>
                                
                                <div className="relative text-center">
                                    <div className="text-6xl mb-3 transform transition-all duration-300 hover:scale-110">
                                        {moods[currentMoodIndex].emoji}
                                    </div>
                                    <div className="text-xl font-semibold text-gray-800">
                                        {moods[currentMoodIndex].label}
                                    </div>
                                </div>

                                {/* Navigation Arrows */}
                                <button
                                    onClick={prevMood}
                                    className="absolute left-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all duration-200 hover:scale-110"
                                >
                                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                                </button>
                                
                                <button
                                    onClick={nextMood}
                                    className="absolute right-4 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all duration-200 hover:scale-110"
                                >
                                    <ChevronRight className="w-5 h-5 text-gray-600" />
                                </button>
                            </div>

                            {/* Mood Indicators */}
                            <div className="flex justify-center space-x-2 mt-4">
                                {moods.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => setCurrentMoodIndex(index)}
                                        className={`w-3 h-3 rounded-full transition-all duration-200 ${
                                            index === currentMoodIndex 
                                                ? 'bg-indigo-500 scale-125' 
                                                : 'bg-gray-300 hover:bg-gray-400'
                                        }`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* End Time */}
                    <div className="mb-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
                            <Clock className="w-5 h-5 mr-2 text-indigo-500" />
                            Finish Time
                        </h3>
                        
                        <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-gray-800 mb-4">
                                    {formatTime(endTime)}
                                </div>
                                
                                <div className="flex justify-center space-x-2">
                                    <button
                                        onClick={() => adjustTime(-15)}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                    >
                                        <span className="text-lg">-15m</span>
                                    </button>
                                    <button
                                        onClick={() => adjustTime(-5)}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                    >
                                        <span className="text-lg">-5m</span>
                                    </button>
                                    <button
                                        onClick={() => adjustTime(5)}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                    >
                                        <span className="text-lg">+5m</span>
                                    </button>
                                    <button
                                        onClick={() => adjustTime(15)}
                                        className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
                                    >
                                        <span className="text-lg">+15m</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Fatigue Level */}
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <Zap className="w-5 h-5 mr-2 text-indigo-500" />
                            Current Fatigue
                        </h3>
                        <div className="bg-white rounded-xl p-4 shadow-lg border border-gray-100">
                            <div className="text-center mb-4">
                                <div className={`inline-block text-sm px-4 py-2 rounded-lg text-white font-semibold bg-gradient-to-r ${getFatigueColor(endFatigue)}`}>
                                    {endFatigue}/10 - {getFatigueLabel(endFatigue)}
                                </div>
                            </div>
                            <div className="relative">
                                <input
                                    type="range"
                                    min="0"
                                    max="10"
                                    step="1"
                                    value={endFatigue}
                                    onChange={(e) => setEndFatigue(parseInt(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                                    style={{
                                        background: `linear-gradient(to right, #10b981 0%, #10b981 ${endFatigue * 10}%, #e5e7eb ${endFatigue * 10}%, #e5e7eb 100%)`
                                    }}
                                />
                                <div className="flex justify-between text-sm text-gray-500 mt-2">
                                    <span>No fatigue</span>
                                    <span>Very high</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Session Feedback */}
                    <div className="mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <MessageSquare className="w-5 h-5 mr-2 text-indigo-500" />
                            Session Feedback
                        </h3>
                        
                        <div className="relative text-lg">
                            <textarea
                                value={session.session_feedback}
                                onChange={(e) => handleFieldChange('session_feedback', e.target.value)}
                                placeholder="How did this session go? What worked well?"
                                className="w-full h-24 p-4 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                            />
                            {/* <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                                {session.session_feedback.length}/200
                            </div> */}
                        </div>
                    </div>

                    {/* Self Reflection */}
                    <div className="mb-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center">
                            <Lightbulb className="w-5 h-5 mr-2 text-indigo-500" />
                            Self Reflection
                        </h3>
                        
                        <div className="relative text-lg">
                            <textarea
                                value={session.session_reflection}
                                onChange={(e) => handleFieldChange('session_reflection', e.target.value)}
                                placeholder="What insights did you gain? How are you feeling now?"
                                className="w-full h-24 p-4 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 bg-white shadow-sm"
                            />
                            {/* <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                                {session.session_reflection.length}/200
                            </div> */}
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-4 sticky bottom-0 bg-gradient-to-t from-slate-50 pt-4">
                        <button
                            onClick={handleSubmit}
                            className="flex-1 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
                        >
                            <Save className="w-5 h-5" />
                            <span className="text-lg">Finish</span>
                        </button>

                        <button
                            onClick={handleCancel}
                            className="flex-1 bg-gradient-to-r from-gray-400 to-gray-500 text-white py-4 px-6 rounded-xl font-semibold shadow-lg hover:shadow-xl transform transition-all duration-200 hover:scale-105 flex items-center justify-center space-x-2"
                        >
                            <X className="w-5 h-5" />
                            <span className="text-lg">Cancel</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CompleteSession;

// import { useState } from "react";
// import { TextField, Button } from "@mui/material";
// import Slider from 'react-slick';
// import 'slick-carousel/slick/slick.css';
// import 'slick-carousel/slick/slick-theme.css';
// import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
// import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
// import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
// import dayjs from 'dayjs';
// import RoundDownToFiveMinutes from "../../../components/utils/RoundTimeToFiveMinutes";

// const CompleteSession = ({startTime, handleCompleteSession, handleCancel}) => {

//     const defaultSlide = 3;

//     const images = [
//         '../img/mood_s/very_bad.png',
//         '../img/mood_s/bad.png',
//         '../img/mood_s/neutral.png',
//         '../img/mood_s/good.png',
//         '../img/mood_s/very_good.png',
//     ];

//     const [session, setSession] = useState(
//         {
//             end_mood: defaultSlide,
//             end_time: dayjs(RoundDownToFiveMinutes(new Date())).isAfter(dayjs(startTime)) ? dayjs(RoundDownToFiveMinutes(new Date())) : dayjs(startTime),
//             session_feedback: '',
//             session_reflection: ''
//         }
//     );
//     const currentTime = dayjs(RoundDownToFiveMinutes(new Date()));
//     const [endTime, setEndTime] = useState(currentTime.isAfter(dayjs(startTime)) ? currentTime : dayjs(startTime));

//     const handleFieldChange = (fieldName, e) => {
//         if (e.target.value === '') {
//             setSession((session) => ({ ...session, [fieldName]: null }));
//         } else {
//             setSession((session) => ({ ...session, [fieldName]: e.target.value }));
//         }
//     };

//     const handleSubmit = () => {
//         // Ensure max session duration is less than a day. If end time is after start time, assume same day. If end time before start time, use next day.
//         const parsedStartTime = dayjs(startTime);
//         const end_date = (endTime.hour() > parsedStartTime.hour() || (endTime.hour() === parsedStartTime.hour() && endTime.minute() > parsedStartTime.minute()))
//             ? parsedStartTime : parsedStartTime.add(1, 'day');

//         setSession((prev) => {
//             const newSession = {
//                 ...session,
//                 end_time: end_date.hour(endTime.hour()).minute(endTime.minute()).second(0).millisecond(0).format('YYYY-MM-DDTHH:mm:ssZ')
//             }

//             handleCompleteSession(newSession);
//             return newSession;
//         })
//     }

//     const settings = {
//         dots: false,
//         infinite: true,
//         speed: 500,
//         slidesToShow: 1, // Number of images to display
//         slidesToScroll: 1,
//         initialSlide: session.end_mood,
//         centerMode: true,
//         centerPadding: '50px',
//         afterChange: (index) => setSession({...session, end_mood: index})
//     };

//     return (
//         <div style={{backgroundColor: '#e0e0e0', borderRadius: '15px', position: 'absolute', top: '0', bottom: '0', margin:'auto 0', height: 'fit-content', maxHeight: '100%', width: '100%', padding: '0 5px', overflowY: 'scroll', overflowX: 'hidden', scrollbarWidth: 'none'}}>
//             <h2 className="h2-style" style={{marginBottom: '15px'}}>Finish session</h2>
            
//             {/* End mood carousel */}
//             <p className="p-style" style={{textAlign: 'start', marginLeft: '10px'}}>How are you feeling?</p>
//             <Slider {...settings}>
//                 {images.map((image, index) => (
//                     <div key={index} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
//                         <img src={image} alt={`Slide ${index + 1}`} />
//                     </div>
//                 ))}
//             </Slider>

//             {/** Session end time */}
//             <LocalizationProvider dateAdapter={AdapterDayjs}>
//                 <DesktopTimePicker 
//                     label="Finish time" 
//                     // minTime={dayjs(startTime)}
//                     value={endTime}
//                     onChange={(time) => {setEndTime(time)}}
//                     slotProps={{
//                         textField: {
//                             fullWidth: true,
//                             InputProps: {
//                             sx: {
//                                 fontSize: '16px', // Increase the font size for the input text
//                                 textAlign: 'center',
//                                 '& input': {
//                                     textAlign: 'center', // This centers the text inside the input field
//                                 },
//                             },
//                             },
//                             InputLabelProps: {
//                             shrink: true,
//                             sx: {
//                                 fontSize: '12px',
//                             },
//                             },
//                             sx: {
//                                 marginTop: '15px',
//                             },
//                         },
//                     }}
//                 />
//             </LocalizationProvider>

//             {/* Feedback textbox */}    
//             <TextField fullWidth style={{ marginTop: "10px" }}
//                 type="text"
//                 multiline
//                 rows={2}
//                 id="feedback"
//                 label="Session Feedback"
//                 variant="outlined"
//                 value={session.feedback}
//                 onChange={(e) => handleFieldChange('session_feedback', e)}
//             />

//             {/* Self reflection textbox */}  
//             <TextField fullWidth style={{ marginTop: "10px" }}
//                 type="text"
//                 multiline
//                 rows = {2}
//                 id="reflection"
//                 label="Self Reflection"
//                 variant="outlined"
//                 value={session.self_reflection}
//                 onChange={(e) => handleFieldChange('session_reflection', e)}
//             />

//             <div className='bottom-buttons'>
//                 <Button
//                     style={{margin: "10px 5px 5px", width: '100px'}}
//                     onClick={handleSubmit}
//                     variant="contained"
//                 >
//                     Save
//                 </Button>

//                 <Button
//                     style={{margin: "10px 5px 5px", width: '100px'}}
//                     onClick={handleCancel}
//                     variant="contained"
//                 >
//                     Cancel
//                 </Button>
//             </div>
//         </div>
//     )

// };

// export default CompleteSession;